/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
import { logger } from "../../utils/logger.js";
/**
 * @fileoverview Requirements page logic module.
 * Handles fetching categories, sectors, certifications and re-exports
 * requirements list operations for the Requirements page.
 * @module logic/requirements
 */

import dayjs from "dayjs";
import {
  getAuthJsonHeaders,
  logDevError,
  resolveExpiredToken,
  uploadFileToPresignedUrl,
} from "../../utils/eventApi.js";
import {
  getUserRoleData as getUserRoleDataFromList,
  getAllValidRequirements,
  getCompanyUnapprovedRequirements,
  getRequirementsFromSingleCompany,
  approveRequirement,
  deleteRequirement,
  updateRequirement,
} from "../requirementsList";

/** localStorage key for current user id */
export const LOCALSTORAGE_USER_ID_KEY = "userId";

/**
 * Whether all mandatory fields for creating a requirement are present.
 * Mandatory: category, subcategory, title, due date, description, location,
 * minimum experience, sector. Subsector, branch, certifications, image, volume are optional.
 * @param {object} requirement
 * @returns {boolean}
 */
export const areRequirementCreateMandatoryFieldsComplete = (requirement) => {
  const trim = (v) => (typeof v === "string" ? v.trim() : "");
  const exp = requirement.experienciaMinima;
  const hasExperience =
    exp !== null && exp !== undefined && String(exp).trim() !== "";
  return (
    Boolean(requirement.idCategoria) &&
    Boolean(requirement.nombreSubcategoria) &&
    Boolean(trim(requirement.titulo)) &&
    Boolean(requirement.fechaLimite) &&
    Boolean(trim(requirement.descripcion)) &&
    Boolean(trim(requirement.ubicacion)) &&
    hasExperience &&
    Boolean(requirement.sector)
  );
};

/**
 * @param {Record<string, unknown>} requirement
 * @returns {Record<string, unknown>}
 */
const buildRequirementCreateBody = (requirement, utcDate) => {
  const { image, ...rest } = requirement;
  return {
    ...rest,
    certificaciones: (requirement.certificaciones || []).filter(
      (c) => c && c._id,
    ),
    userId: localStorage.getItem(LOCALSTORAGE_USER_ID_KEY),
    fechaLimite: utcDate,
    nombreImagen: requirement.nombreImagen || undefined,
    formatoImagen: requirement.formatoImagen || undefined,
  };
};

/**
 * Creates a requirement via API (POST) and uploads the image to S3 when a presigned URL is returned.
 * @async
 * @param {object} params
 * @param {object} params.requirement - Requirement form state
 * @param {File|null} [params.imageFile] - Selected image file for presigned upload
 * @param {string|null} [params.nombreImagen] - Original filename when imageFile is set
 * @param {string|null} [params.formatoImagen] - MIME type when imageFile is set
 * @param {Function} params.navigate - React Router navigate
 * @param {Function} params.onCreateRequirement - Called with created requirement on success
 * @param {Function} params.onBack - Called after successful create
 * @returns {Promise<{ status: 'success' } | { status: 'validation_error' } | { status: 'no_token' } | { status: 'session_expired' } | { status: 'error' }>}
 */
const REQUIREMENT_DEADLINE_END_HOUR = 23;
const REQUIREMENT_DEADLINE_END_MINUTE = 59;
const REQUIREMENT_DEADLINE_END_SECOND = 59;

export const handleCreateRequirement = async ({
  requirement,
  imageFile,
  nombreImagen,
  formatoImagen,
  navigate,
  onCreateRequirement,
  onBack,
}) => {
  if (!areRequirementCreateMandatoryFieldsComplete(requirement)) {
    return { status: "validation_error" };
  }

  const authToken = localStorage.getItem("token");
  if (!authToken || authToken === "undefined") {
    logger.error("Failed to get the token");
    return { status: "no_token" };
  }

  const utcDate = dayjs(requirement.fechaLimite)
    .hour(REQUIREMENT_DEADLINE_END_HOUR)
    .minute(REQUIREMENT_DEADLINE_END_MINUTE)
    .second(REQUIREMENT_DEADLINE_END_SECOND)
    .utc("z")
    .toISOString();
  const requirementData = buildRequirementCreateBody(
    {
      ...requirement,
      nombreImagen: imageFile ? nombreImagen : null,
      formatoImagen: imageFile ? formatoImagen : null,
    },
    utcDate,
  );

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}requirement`,
      {
        method: "POST",
        headers: getAuthJsonHeaders(),
        body: JSON.stringify(requirementData),
      },
    );

    const responseData = await response.json();
    const tokenResult = await resolveExpiredToken(responseData, navigate, () =>
      handleCreateRequirement({
        requirement,
        imageFile,
        nombreImagen,
        formatoImagen,
        navigate,
        onCreateRequirement,
        onBack,
      }),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return { status: "session_expired" };
      }
      return tokenResult.result;
    }

    if (response.ok && responseData.requirement) {
      if (responseData.presignedUrl && imageFile) {
        await uploadFileToPresignedUrl(
          responseData.presignedUrl,
          imageFile,
          formatoImagen,
        );
      }
      onCreateRequirement(responseData.requirement);
      onBack();
      return { status: "success" };
    }

    logDevError(
      "Failed to create requirement:",
      response.status,
      responseData?.message,
    );
    return { status: "error" };
  } catch (error) {
    logDevError("Error creating requirement:", error);
    return { status: "error" };
  }
};

/**
 * Fetches active categories.
 * @async
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Array>} Array of active categories
 */
export const getActiveCategories = async (navigate) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}category/active`,
      { method: "GET", headers: getAuthJsonHeaders() },
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      getActiveCategories(navigate),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return [];
      }
      return tokenResult.result;
    }
    return Array.isArray(data) ? data : [];
  } catch (error) {
    logDevError(error);
    return [];
  }
};

/**
 * Fetches sectors.
 * @async
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Array>} Array of sectors
 */
export const getSectors = async (navigate) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_ROOT_API}sector`, {
      method: "GET",
      headers: getAuthJsonHeaders(),
    });
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      getSectors(navigate),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return [];
      }
      return tokenResult.result;
    }
    return Array.isArray(data) ? data : [];
  } catch (error) {
    logDevError(error);
    return [];
  }
};

/**
 * Fetches certifications.
 * @async
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Array>} Array of certifications
 */
export const getCertifications = async (navigate) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}certification`,
      { method: "GET", headers: getAuthJsonHeaders() },
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      getCertifications(navigate),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return [];
      }
      return tokenResult.result;
    }
    return Array.isArray(data) ? data : [];
  } catch (error) {
    logDevError(error);
    return [];
  }
};

/**
 * Fetches and applies user role/permission data.
 * @async
 * @param {string} userId - User identifier
 * @param {Function} navigate - React Router navigation function
 * @param {Function} changePermissions - Callback to update permissions state
 * @returns {Promise<void>}
 */
export const getUserRoleData = getUserRoleDataFromList;

export {
  getAllValidRequirements,
  getCompanyUnapprovedRequirements,
  getRequirementsFromSingleCompany,
  approveRequirement,
  deleteRequirement,
  updateRequirement,
};
